import React from 'react';
import { createPortal } from 'react-dom';
import type { PublishedTariffRate } from '../../../services/quote.service';

interface Props {
  rows: PublishedTariffRate[];
  onClose: () => void;
}

/**
 * Read-only "Tariff Rates" reference popup (N61): the published default +
 * minimum rates for each charge (GET /quotes/tariff-rates). Shared by the
 * Quote Table Maintenance page and the Additional Charges card.
 *
 * Portaled to document.body so its `position: fixed` overlay covers the full
 * viewport even when opened from inside a transformed ancestor (e.g. the
 * lane-editor drawer panel, whose CSS transform would otherwise confine it).
 */
const TariffRatesPopup: React.FC<Props> = ({ rows, onClose }) => createPortal(
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4"
    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div role="dialog" aria-label="Tariff Rates" className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-2xl border border-slate-200 dark:border-slate-700">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <h2 className="text-[0.875rem] font-semibold text-slate-800 dark:text-slate-100">Tariff Rates</h2>
          <p className="m-0 text-[0.71875rem] text-slate-500 dark:text-slate-400">Published default &amp; minimum rates for each charge.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">×</button>
      </div>
      <div className="px-4 py-3 max-h-[60vh] overflow-y-auto">
        <table className="w-full text-[0.75rem]">
          <thead>
            <tr className="text-left text-slate-500 dark:text-slate-400">
              <th className="py-1">Source</th><th>Description</th><th>Rate Type</th>
              <th className="text-right">Default</th><th className="text-right">Min</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-slate-400 dark:text-slate-500">No tariff rates.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200">
                <td className="py-1 text-[0.6875rem] text-slate-500 dark:text-slate-400">{r.source}</td>
                <td>{r.description}</td>
                <td>{r.ratetype || '—'}</td>
                <td className="text-right">{Number(r.defaultRate).toFixed(2)}</td>
                <td className="text-right">{Number(r.minimumRate).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>,
  document.body,
);

export default TariffRatesPopup;
